"use client";
import { useSnackbarContext } from "@repo/contexts/SnackbarContext";
import { useFormValidation } from "@repo/lib/hooks/common";
import {
  useUserAttributes,
  useUserLoginEvent,
} from "@repo/lib/hooks/webengage";
import { getUserSessionServer, validation } from "@repo/lib/utils";
import { authenticate, authenticateSocial } from "@repo/server-actions/account";
import { addToWishlist } from "@repo/server-actions/wishlist";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState, useTransition } from "react";

const PASSWORD_FIELDS = { username: "", password: "" };
const OTP_FIELDS = { email: "" };

const useAuth = ({
  /** "password" | "otp" */
  mode = "password",
  /** "primary"  | "secondary" — checkout flow. */
  variant = "primary",
  handleCheckoutSignIn,
}) => {
  const isOtp = mode === "otp";
  const initialFields = isOtp ? OTP_FIELDS : PASSWORD_FIELDS;

  const { formValues, formErrors, setFormErrors, handleValidation } =
    useFormValidation(initialFields);

  /* shared plumbings */

  const [otpValue, setOtpValue] = useState("");
  const [isPending, startTransition] = useTransition();
  const [isSocialLoginPending, startSocialLoginTransition] = useTransition();

  const router = useRouter();
  const locale = useLocale();
  const tCommon = useTranslations("Common");
  const { setSnackbar } = useSnackbarContext();

  const searchParams = useSearchParams();
  const pageRedirectUrl = searchParams.get("redirect"); // redirection is to handle redirection to wishlist page
  const productId = searchParams.get("productId"); //product ID to add this item to wishlist if the user is redirected to login page if he clicked on add to wishlist button as a guest user

  const { handleUserLoginEvent } = useUserLoginEvent();
  const { handleSetUserAttributes } = useUserAttributes();

  const submitForm = useCallback(async () => {
    const payLoad = isOtp
      ? { email: formValues.email, otp: otpValue }
      : { username: formValues.username, password: formValues.password };

    startTransition(async () => {
      const response = await authenticate(payLoad);
      const session = await getUserSessionServer();
      if (response?.success) {
        // If the user is successfully logged in, we can send the login event to WebEngage
        const emailToTrack = isOtp ? payLoad.email : payLoad.username;
        await handleUserLoginEvent(emailToTrack);
        await handleSetUserAttributes(session);
        // Below sign in function and variant check if for login from checkout page
        if (variant === "secondary") {
          handleCheckoutSignIn();
          router.refresh(); // TODO: router refresh must be removed once we have the solution to refresh the layout
        }
        if (pageRedirectUrl) {
          // If the guest user is attempting to add the item to wishlist
          // Add the item to wishlist once he is logged in and redirect to previous url
          try {
            const wishlistRes = await addToWishlist(productId, locale);
            setSnackbar({
              open: true,
              message: wishlistRes?.message,
              type: wishlistRes?.success ? "success" : "error",
            });
            router.push(pageRedirectUrl);
          } catch (error) {
            console.error("Error adding to wishlist:", error);
          }
        } else {
          // If the customer is trying to login from checkout and only refresh the page so that they can stay on checkout
          if (variant === "secondary") {
            router.refresh();
          } else {
            // If the customer directing trying to login from the login page and does not add item to wishlist then
            // redirect to homepage with window to avoid router refresh which will refresh the complete layout and created problem with displaying homepage
            window.location.assign("/account");
          }
        }
      }
      setSnackbar({
        open: true,
        message: response?.customMessage
          ? tCommon(response?.message)
          : response?.message,
        type: response?.success ? "success" : "error",
      });
    });
  }, [
    isOtp,
    otpValue,
    formValues,
    variant,
    handleCheckoutSignIn,
    router,
    pageRedirectUrl,
    productId,
    locale,
    setSnackbar,
    tCommon,
    handleUserLoginEvent,
    handleSetUserAttributes,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validation(formValues, tCommon);
    if (Object.keys(errors).length === 0) {
      submitForm();
    } else {
      setFormErrors(errors);
      console.log("Failed to submit the form, please check the form fields");
    }
  };

  const handleSocialSubmit = (typeOfLogin) => {
    startSocialLoginTransition(async () => {
      await authenticateSocial({ typeOfLogin });
    });
  };

  const handleSuccessfulSocialLogin = useCallback(async () => {
    const session = await getUserSessionServer();
    if (session && session?.user?.socialType !== "none") {
      await handleUserLoginEvent(session.email);
      await handleSetUserAttributes(session);
      if (variant === "secondary") {
        handleCheckoutSignIn();
        router.refresh(); // TODO: router refresh must be removed once we have the solution to refresh the layout
      }
      if (pageRedirectUrl) {
        // If the guest user is attempting to add the item to wishlist
        // Add the item to wishlist once he is logged in and redirect to previous url
        try {
          const wishlistRes = await addToWishlist(productId, locale);
          setSnackbar({
            open: true,
            message: wishlistRes?.message,
            type: wishlistRes?.success ? "success" : "error",
          });
          router.push(pageRedirectUrl);
        } catch (error) {
          console.error("Error adding to wishlist:", error);
        }
      } else {
        // If the customer is trying to login from checkout and only refresh the page so that they can stay on checkout
        if (variant === "secondary") {
          router.refresh();
        } else {
          // If the customer directing trying to login from the login page and does not add item to wishlist then
          // redirect to homepage with window to avoid router refresh which will refresh the complete layout and created problem with displaying homepage
          window.location.assign("/account");
        }
      }

      setSnackbar({
        open: true,
        message: tCommon("You_Are_Successfully_Logged_In"),
        type: "success",
      });
    }
    return;
  }, [
    handleUserLoginEvent,
    handleSetUserAttributes,
    setSnackbar,
    tCommon,
    handleCheckoutSignIn,
    locale,
    productId,
    pageRedirectUrl,
    router,
    variant,
  ]);

  useEffect(() => {
    handleSuccessfulSocialLogin();
  }, [handleSuccessfulSocialLogin]);

  return {
    handleSubmit,
    handleValidation,
    formErrors,
    isPending,
    isSocialLoginPending,
    otpValue,
    setOtpValue,
    formValues,
    setFormErrors,
    handleSocialSubmit,
  };
};

export default useAuth;
